import React, { useState, useCallback, ReactNode, FC } from 'react';
import { GoalsContext, Goal } from './GoalsContext';
import { route, GoalType } from '../utils/apiRoutes';
import { sortGoals } from '../utils/sortUtils';
import { transformGoalFromList, transformGoalPreview } from '../utils/transformApiResponse';
import { isEuSdgEnabled } from '../utils/featureFlags';

// Initialize goalType based on current URL (EU only when feature flag is on)
const getInitialGoalType = (): GoalType => {
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (isEuSdgEnabled && (path === '/eu-sdg' || path.startsWith('/eu-sdg/'))) {
      return 'eu';
    }
  }
  return 'un';
};

const GoalsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [goalType, setGoalType] = useState<GoalType>(getInitialGoalType());
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  // Placeholder global/EU lists for now
  const globalGoals: Goal[] = [];
  const euGoals: Goal[] = [];

  // Fetch all goals using batch preview endpoint (optimized - single API call)
  const fetchGoals = useCallback(async (typeOverride?: GoalType) => {
    const activeType = typeOverride ?? goalType;
    setLoading(true);
    setError(null);
    try {
      const batchUrl = route.goalsPreviewBatch(activeType, false);
      const batchResponse = await fetch(batchUrl);
      
      if (!batchResponse.ok) {
        throw new Error(`Failed to fetch goals batch: ${batchResponse.status}`);
      }
      
      const batchData = await batchResponse.json();
      const goalsArray = batchData.goals || [];
      
      if (!Array.isArray(goalsArray) || goalsArray.length === 0) {
        setGoals([]);
        setInitialLoading(false);
        return;
      }
      
      console.log(`[GoalsProvider] Processing ${goalsArray.length} goals from batch endpoint`);
      const startTransform = performance.now();
      
      // Transform batch response to Goal format
      const transformedGoals = goalsArray.map((preview: any) => transformGoalPreview(preview));
      const validGoals = transformedGoals.filter((g): g is Goal => g !== null);
      const sortedGoals = sortGoals(validGoals) as Goal[];
      
      const transformTime = performance.now() - startTransform;
      console.log(`[GoalsProvider] Transformed ${validGoals.length} goals in ${Math.round(transformTime)}ms`);
      
      setGoals(sortedGoals);
    } catch (error: any) {
      console.error('Error fetching goals:', error);
      const errorMessage = error?.message || 'Failed to connect to the data server';
      setError(errorMessage);
      setGoals([]);
      setInitialLoading(false);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [goalType]); // Re-fetch when goalType changes to get correct computed values

  const fetchGoal = useCallback(async (goalId: number): Promise<Goal | null> => {
    // Clear selected goal first to prevent stale data when switching
    setSelectedGoal(null);
    setLoading(true);
    try {
      // Fetch preview with goalType context - API returns different computed values for EU vs UN
      const response = await fetch(route.goalPreview(goalId, false, goalType));
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const preview = await response.json();
      const transformedGoal = transformGoalPreview(preview);
      setSelectedGoal(transformedGoal);
      return transformedGoal;
    } catch (error) {
      console.error('Error fetching single goal:', error);
      setSelectedGoal(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [goalType]);

  const fetchAllGoals = async () => {
    // Not implemented - using fetchGoals instead
  };

  return React.createElement(
    GoalsContext.Provider,
    {
      value: {
        goals,
        globalGoals,
        euGoals,
        loading,
        initialLoading,
        error,
        goalType,
        setGoalType,
        selectedGoal,
        setSelectedGoal,
        fetchGoals,
        fetchGoal,
        fetchAllGoals,
      },
    },
    children
  );
};

export { GoalsProvider };
